// frontend/src/features/agency/AgencyReservedDriverList.js
// Phase 8.7C: Agency Reserved lane
//
// Key principles:
// - Membership is assignment-derived (requested/confirmed assignments), NOT driver.status.
// - This screen is a read view for already reserved drivers.
// - New reservation requests should be initiated from the Available lane.
// - We render from local `rows` (source of truth for this lane).
// - We optionally upsert into driverSlice as an entity cache.
// - This component does NOT subscribe to socket events anymore.
// - Real-time invalidation + stats bump are owned by services/socket.js.

import React, { useMemo, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { upsertDrivers, removeDriver } from '../drivers/driverSlice';
import { selectQuery, selectStatsRefreshKey } from '../ui/filtersSlice';

import { fetchAgencyReservedDrivers } from '../../services/driverService';

import axios from 'axios';

import DriverEditModal from '../drivers/DriverEditModal';
import { Trash2, Edit2 } from 'lucide-react';

import { selectAgencyIdForUser } from '../auth/authSlice';

function filterRowsByQuery(rows, q) {
  const needle = (q ?? '').trim().toLowerCase();
  if (!needle) return rows || [];

  return (rows || []).filter((row) => {
    const haystacks = [
      row?.name,
      row?.vehicle_registration_number,
      row?.assignment_status,
      row?.assignment_id != null ? String(row.assignment_id) : null,
      row?.ban_reason,
      row?.reason,
      row?.ban_scope,
      row?.ban_site_id != null ? String(row.ban_site_id) : null,
      row?.ban_id != null ? String(row.ban_id) : null,
    ];

    return haystacks.some((v) => String(v ?? '').toLowerCase().includes(needle));
  });
}

export default function ReservedDriverList() {
  const dispatch = useDispatch();
  const agencyId = useSelector(selectAgencyIdForUser);
  const statsKey = useSelector(selectStatsRefreshKey);
  const q = useSelector(selectQuery);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);

  async function reload() {
    const list = await fetchAgencyReservedDrivers();
    setRows(list);

    // Optional: keep entity cache warm for edit modal / details
    dispatch(upsertDrivers(list));
  }

  useEffect(() => {
    if (!agencyId) return;

    (async () => {
      try {
        await reload();
      } finally {
        setLoading(false);
      }
    })();
  }, [agencyId, statsKey]);

  const filtered = useMemo(() => filterRowsByQuery(rows, q), [rows, q]);
  const sorted = useMemo(() => [...filtered], [filtered]);

  const handleDeleteClick = async (id) => {
    try {
      await axios.delete(`/agencies/drivers/${id}`);
      dispatch(removeDriver(id));
      await reload();
    } catch (err) {
      console.error('Error deleting driver:', err);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading…</div>;
  }

  return (
    <div className="my-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-bold text-gray-800">Reserved Drivers</h3>
      </div>

      {q?.trim() && sorted.length === 0 && (
        <div className="text-sm text-gray-500 py-3">
          No matches for “{q.trim()}”.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              {['Name', 'Vehicle Reg.', 'Assignment', 'Actions'].map((col) => (
                <th
                  key={col}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {sorted.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">{row.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {row.vehicle_registration_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {row.assignment_status
                    ? `${row.assignment_status} (#${row.assignment_id})`
                    : '—'}
                </td>

                <td className="px-6 py-4">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setSelectedDriver(row)}
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>

                    <button
                      onClick={() => handleDeleteClick(row.id)}
                      className="p-1 hover:bg-gray-100 rounded text-red-600"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedDriver && (
        <DriverEditModal
          driver={selectedDriver}
          onClose={() => setSelectedDriver(null)}
          onUpdate={async () => {
            setSelectedDriver(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}